import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../utils/api-handler.util.js'
import { showToaster } from '../../utils/show-toaster.util.js'
import SubmittedMessage from './SubmittedMessage.jsx'
import { useAuth } from '../../contexts/AuthContext'
import useProductCategories from '../../hooks/useProductCategories.js'
import { useFileUpload } from '../../hooks/useFileUpload.jsx'
import { QueryKeys } from '../../utils/queryKeyGenerator.js'
import InputField from '../common/InputField.jsx'
import SelectField from '../common/SelectField.jsx'
import TextareaField from '../common/TextareaField.jsx'
import DisplayForUserType from '../auth/DisplayForUserType.jsx'
import USER_TYPE from '../../constants/userType.js'
import { MAX_IMAGE_SIZE_BYTES, MAX_IMAGES_PER_PRODUCT } from '../../constants/retailer.js'

const RetailerProductForm = () => {
  const { renderUploadInput, handleFileUpload, isUploading } = useFileUpload({
    label: 'Upload Product Images',
    dropZoneOptions: {
      multiple: true,
      maxSize: MAX_IMAGE_SIZE_BYTES,
      maxFiles: MAX_IMAGES_PER_PRODUCT,
      accept: {
        'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
      },
    },
  })
  const { categories } = useProductCategories()
  const [productName, setProductName] = useState('')
  const [productPrice, setProductPrice] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [newProductSlug, setNewProductSlug] = useState(null)
  const [minOrderQuantity, setMinOrderQuantity] = useState('')

  const { currentUser } = useAuth()

  const retailerProductMutation = useMutation({
    mutationFn: (formData) => handlePostRequest(`/${currentUser?.userType}s/my-products`, formData),
    onSuccess: (data) => {
      showToaster('success', 'Product added successfully')
      QueryKeys.listShopProducts.invalidate(currentUser?.userType)
      const createdProductSlug = data?.data?.productSlug
      setIsSubmitted(true)

      if (createdProductSlug) {
        setNewProductSlug(createdProductSlug)
      }

      setProductName('')
      setProductPrice('')
      setSelectedCategory('')
      setDescription('')
    },
  })

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const productImages = await handleFileUpload()

      await retailerProductMutation.mutateAsync({
        name: productName,
        price: productPrice,
        categoryId: selectedCategory,
        description: description,
        productImages,
        minOrderQuantity,
      })
      QueryKeys.listShopProducts.invalidate(currentUser?.userType)
    } catch (error) {
      showToaster('error', 'Failed to add product. Please try again.')
    }
  }

  const isBusy = retailerProductMutation.isLoading || isUploading

  return isSubmitted ? (
    <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
      <SubmittedMessage
        message="Product created"
        buttonMessage="View Product"
        redirectPath={`/${currentUser.userType}/products/${newProductSlug}`}
      />
    </div>
  ) : (
    <div>
      <form onSubmit={handleSubmit} className="mt-4">
        <fieldset disabled={retailerProductMutation.isLoading} className="flex flex-col gap-4">
          <div>
            <InputField
              id="productName"
              name="productName"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              required
              label={'Product Name'}
            />
          </div>
          <div>
            <label
              htmlFor="category"
              className="block text-sm font-medium text-gray-600 mb-2 font-inter"
            >
              Select Product Category <span className="text-red-500">*</span>
            </label>
            <SelectField
              id="category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              required
            >
              <option value="" disabled className="font-inter">
                Select a category
              </option>
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </SelectField>
          </div>
          <div className="">
            <InputField
              id="productPrice"
              name="productPrice"
              type="number"
              value={productPrice}
              onChange={(e) => setProductPrice(e.target.value)}
              required
              placeholder="Enter product price"
              label={'Product Price (Ksh)'}
            />
          </div>
          <DisplayForUserType userTypes={[USER_TYPE.wholeseller]}>
            <div className="">
              <InputField
                id="minOrderQuantity"
                name="minOrderQuantity"
                type="number"
                value={minOrderQuantity}
                onChange={(e) => setMinOrderQuantity(e.target.value)}
                required
                placeholder="Enter minimum order quantity"
                label={'Minimum Order Quantity'}
              />
            </div>
          </DisplayForUserType>
          <div className="">
            <TextareaField
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              label={'Tell Us More About The Product'}
              placeholder="Describe the product you are creating"
            />
          </div>
          <div className="">
            {renderUploadInput()}

            <p className="text-sm text-foreground-muted">
              Image needs to be between 500x500 pixels and 1000x1000 pixels in JPG, PNG, or WEBP
              format. The maximum file size allowed is {MAX_IMAGE_SIZE_BYTES / 1024 / 1024}MB. You
              can upload upto {MAX_IMAGES_PER_PRODUCT} images per product.
            </p>
          </div>
          <div className="pt-4 mt-8">
            <button
              type="submit"
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
                isBusy ? 'opacity-70 cursor-wait' : ''
              }`}
              disabled={isBusy}
            >
              {isBusy ? 'Submitting...' : 'Add'}
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  )
}

export default RetailerProductForm
